import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SynchronizationFormComponent } from '../synchronization-form/synchronization-form.component';
import {
  SynchronizationFormConfig,
  SYNCHRONIZATION_HEADER_ACTIONS_VIEW,
  createSynchronizationViewFormFields,
  SYNCHRONIZATION_TABLE_SECTIONS_VIEW,
  SYNCHRONIZATION_BASE_COLUMNS,
  SYNCHRONIZATION_TABLE_COLUMNS,
  getAdjustmentTypeText,
  getAdjustmentTypeClass,
} from '../../synchronization.config';
import { DataTableComponent, TableColumn } from '@shared/components/data-table/data-table.component';  
import { SynchronizationItem } from '../../models/synchronization.model';
import { SynchronizationService } from '../../services/synchronization.service';
import { extractErrorMessage, getErrorSeverity, getErrorTitle } from '@shared/utils/error-handler.util';
import { parsePositiveIntRouteParam } from '@shared/utils/route-param.util';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-synchronization-view',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    TranslateModule,
    SynchronizationFormComponent,
    DataTableComponent
  ],
  providers: [SynchronizationService, MessageService],
  templateUrl: './synchronization-view.component.html',
  styleUrls: ['./synchronization-view.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SynchronizationViewComponent implements OnInit {
  config: SynchronizationFormConfig = {
    title: 'synchronization.form.view.title',
    formFields: createSynchronizationViewFormFields(),
    headerActions: SYNCHRONIZATION_HEADER_ACTIONS_VIEW, 
    tableSections: [], // No table sections in view mode - we add table separately below
    listRoute: '/stock/synchronization',
  };

  // Data signals
  synchronizationData = signal<SynchronizationItem | null>(null);
  loading = signal(false);

  height: string = 'auto';

  viewTableColumns: TableColumn[] = SYNCHRONIZATION_TABLE_COLUMNS;
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private synchronizationService: SynchronizationService,
    private messageService: MessageService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadSynchronizationData();
  }

  private loadSynchronizationData(): void {
    const synchronizationId = parsePositiveIntRouteParam(
      this.route.snapshot.queryParams['id'],
    );

    if (synchronizationId == null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('synchronization.messages.error.title'),
        detail: this.translateService.instant('synchronization.messages.error.no_id_provided'),
        life: 3000
      });
      return;
    }

    this.loading.set(true);
    this.synchronizationService.getSynchronizationById(String(synchronizationId)).subscribe({
      next: (synchronization) => {
        this.loading.set(false);
        if (synchronization) {
          // Transform the data to ensure adjustmentType is properly mapped
          const transformedData = {
            ...synchronization,
            adjustDetailsReqDTOList: synchronization.adjustDetailsReqDTOList?.map(item => ({
              ...item,
              adjustmentType: (() => {
                // Use the same logic as the form component
                return (item as any).type === 1 ? 'INCREASE' as const : (item as any).type === 0 ? 'DECREASE' as const : item.adjustmentType;
              })()
            }))
          };
          this.synchronizationData.set(transformedData);
        }
      },
      error: (error) => {
        this.loading.set(false);
        // Show server error message in toast
        const errorMessage = extractErrorMessage(error);
        const errorTitle = getErrorTitle(error);
        const errorSeverity = getErrorSeverity(error);

        this.messageService.add({
          severity: errorSeverity,
          summary: errorTitle,
          detail: errorMessage,
          life: 3000
        });

      },
    });
  }

  // Helper methods for adjustment type display
  getAdjustmentTypeText(type: string): string {
    const translationKey = getAdjustmentTypeText(type);
    return this.translateService.instant(translationKey);
  }

  getAdjustmentTypeClass(type: string): string {
    return getAdjustmentTypeClass(type);
  }
}

